import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Building2, Link2, RefreshCw, Unlink, Filter, X, CheckCircle2, XCircle, Clock, Building } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

type ConnectionStatus = 'all' | 'connected' | 'not_connected';

export default function BankConnections() {
  const [filterSciId, setFilterSciId] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<ConnectionStatus>('all');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle callback from Bridge Connect
  useEffect(() => {
    const bridgeSuccess = searchParams.get('bridge_success');
    const bridgeError = searchParams.get('bridge_error');
    const sciId = searchParams.get('sci_id');

    if (bridgeSuccess === 'true') {
      toast.success('Banque connectée avec succès !');
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['scis-with-bridge-connections'] });
      // Clear URL params
      setSearchParams({});
    } else if (bridgeError) {
      toast.error(`Erreur de connexion: ${bridgeError}`);
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, queryClient, setSearchParams]);

  // Fetch all SCIs with their bridge connection status
  const { data: scis = [], isLoading } = useQuery({
    queryKey: ['scis-with-bridge-connections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scis')
        .select(`
          id,
          name,
          bridge_connected,
          bridge_last_sync,
          bridge_connections (
            id,
            status,
            last_sync_at,
            last_sync_status,
            connected_at,
            error_message
          ),
          bank_accounts (
            id,
            account_name,
            iban,
            is_active,
            bridge_connection_id
          )
        `)
        .order('name');

      if (error) throw error;
      console.log('SCIs with connections:', data);
      return data;
    },
  });

  // Initiate Bridge Connect for a SCI
  const connectBank = useMutation({
    mutationFn: async (sciId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bridge-connect-init`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ sci_id: sciId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to initialize Bridge connection');
      }

      const data = await response.json();
      return data;
    },
    onSuccess: (data) => {
      // Open Bridge Connect URL in new window
      window.open(data.connect_url, '_blank', 'width=600,height=800');
      toast.success('Connexion à la banque initialisée. Veuillez compléter la connexion dans la nouvelle fenêtre.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la connexion à la banque');
    },
  });

  // Sync transactions for a bank account
  const syncTransactions = useMutation({
    mutationFn: async (bankAccountId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bridge-sync-transactions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ bank_account_id: bankAccountId }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to sync transactions');
      }

      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scis-with-bridge-connections'] });
      toast.success(`${data.new_transactions} nouvelle(s) transaction(s) synchronisée(s)`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la synchronisation');
    },
  });

  // Sync all transactions for a SCI (all bank accounts)
  const syncSciTransactions = useMutation({
    mutationFn: async (sciId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Get all bank accounts for this SCI
      const { data: bankAccounts, error: accountsError } = await supabase
        .from('bank_accounts')
        .select('id')
        .eq('sci_id', sciId)
        .eq('is_active', true);

      if (accountsError) throw accountsError;
      if (!bankAccounts || bankAccounts.length === 0) {
        throw new Error('Aucun compte bancaire trouvé');
      }

      let totalNewTransactions = 0;

      // Sync transactions for each bank account
      for (const account of bankAccounts) {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bridge-sync-transactions`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ bank_account_id: account.id }),
          }
        );

        if (response.ok) {
          const result = await response.json();
          totalNewTransactions += result.new_transactions || 0;
        }
      }

      return { new_transactions: totalNewTransactions, accounts_synced: bankAccounts.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scis-with-bridge-connections'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      toast.success(`${data.new_transactions} transaction(s) synchronisée(s) depuis ${data.accounts_synced} compte(s)`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la synchronisation');
    },
  });

  // Disconnect bank connection
  const disconnectBank = useMutation({
    mutationFn: async (sciId: string) => {
      const { data: sci } = await supabase
        .from('scis')
        .select('bridge_connections(id)')
        .eq('id', sciId)
        .single();

      if (!sci?.bridge_connections || !Array.isArray(sci.bridge_connections)) {
        throw new Error('No connection found');
      }

      const connectionId = sci.bridge_connections[0]?.id;
      if (!connectionId) throw new Error('No connection ID');

      const { error } = await supabase
        .from('bridge_connections')
        .update({ status: 'disconnected' })
        .eq('id', connectionId);

      if (error) throw error;

      // Update SCI bridge_connected flag
      await supabase
        .from('scis')
        .update({ bridge_connected: false })
        .eq('id', sciId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scis-with-bridge-connections'] });
      toast.success('Connexion bancaire déconnectée');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la déconnexion');
    },
  });

  // Filter SCIs
  const filteredScis = scis.filter((sci: any) => {
    if (filterSciId !== 'all' && sci.id !== filterSciId) return false;

    if (filterStatus !== 'all') {
      const hasAccounts = sci.bank_accounts?.length > 0;
      const isConnected = sci.bridge_connected || hasAccounts || sci.bridge_connections?.length > 0;
      if (filterStatus === 'connected' && !isConnected) return false;
      if (filterStatus === 'not_connected' && isConnected) return false;
    }

    return true;
  });

  const hasActiveFilters = filterSciId !== 'all' || filterStatus !== 'all';

  const getConnectionStatus = (sci: any) => {
    const connection = sci.bridge_connections?.[0];
    const hasAccounts = sci.bank_accounts?.length > 0;
    
    // Consider connected if we have bank accounts OR bridge_connected is true
    if (!connection && !hasAccounts && !sci.bridge_connected) {
      return { status: 'not_connected', label: 'Non connecté', color: 'secondary' };
    }

    if (connection?.status === 'active' || hasAccounts || sci.bridge_connected) {
      return { status: 'connected', label: 'Connecté', color: 'default' };
    }

    if (connection?.status === 'error') {
      return { status: 'error', label: 'Erreur', color: 'destructive' };
    }

    return { status: 'pending', label: 'En attente', color: 'secondary' };
  };

  const getBankAccountCount = (sci: any) => {
    if (!sci.bank_accounts) return 0;
    return sci.bank_accounts.filter((acc: any) => acc.is_active).length;
  };

  return (
    <AppLayout title="Connexions Bancaires" description="Gérez les connexions bancaires de vos SCI">
      <PageHeader
        title="Connexions Bancaires"
        description={`${filteredScis.length} SCI${filteredScis.length > 1 ? 's' : ''}${hasActiveFilters ? ' (filtrées)' : ''}`}
      />

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-muted/50 rounded-lg flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">SCI:</Label>
          <Select value={filterSciId} onValueChange={setFilterSciId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Toutes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les SCI</SelectItem>
              {scis.map((sci: any) => (
                <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Statut:</Label>
          <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value as ConnectionStatus)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="connected">Connectées</SelectItem>
              <SelectItem value="not_connected">Non connectées</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterSciId('all');
              setFilterStatus('all');
            }}
          >
            <X className="h-4 w-4 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      {/* SCI Cards Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-muted rounded w-2/3"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredScis.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {hasActiveFilters ? 'Aucune SCI ne correspond aux filtres' : 'Aucune SCI trouvée'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredScis.map((sci: any) => {
            const connectionStatus = getConnectionStatus(sci);
            const accountCount = getBankAccountCount(sci);
            const connection = sci.bridge_connections?.[0];
            const hasAccounts = sci.bank_accounts?.length > 0;
            const isConnected = sci.bridge_connected || hasAccounts || connection?.status === 'active';

            return (
              <Card key={sci.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{sci.name}</CardTitle>
                    </div>
                    <Badge
                      variant={connectionStatus.color as any}
                      className="flex items-center gap-1"
                    >
                      {connectionStatus.status === 'connected' && <CheckCircle2 className="h-3 w-3" />}
                      {connectionStatus.status === 'not_connected' && <XCircle className="h-3 w-3" />}
                      {connectionStatus.status === 'pending' && <Clock className="h-3 w-3" />}
                      {connectionStatus.label}
                    </Badge>
                  </div>
                  {isConnected && sci.bank_accounts?.length > 0 && (
                    <CardDescription>
                      {sci.bank_accounts[0].account_name}
                      {sci.bank_accounts[0].iban &&
                        ` (...${sci.bank_accounts[0].iban.slice(-4)})`
                      }
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Connection Info */}
                  <div className="space-y-2 text-sm">
                    {isConnected ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Comptes actifs:</span>
                          <span className="font-medium">{accountCount}</span>
                        </div>
                        {connection?.last_sync_at && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Dernière sync:</span>
                            <span className="font-medium">
                              {format(new Date(connection.last_sync_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                            </span>
                          </div>
                        )}
                        {connection?.error_message && (
                          <div className="text-xs text-destructive mt-2 p-2 bg-destructive/10 rounded">
                            {connection.error_message}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        Connectez cette SCI à votre banque pour synchroniser automatiquement les transactions.
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 pt-2">
                    {!isConnected ? (
                      <Button
                        onClick={() => connectBank.mutate(sci.id)}
                        disabled={connectBank.isPending}
                        className="w-full"
                      >
                        <Link2 className="mr-2 h-4 w-4" />
                        {connectBank.isPending ? 'Connexion...' : 'Connecter la banque'}
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => syncSciTransactions.mutate(sci.id)}
                          disabled={syncSciTransactions.isPending}
                          className="w-full"
                        >
                          <RefreshCw className={`mr-2 h-4 w-4 ${syncSciTransactions.isPending ? 'animate-spin' : ''}`} />
                          {syncSciTransactions.isPending ? 'Synchronisation...' : 'Synchroniser les transactions'}
                        </Button>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" className="w-full">
                              <Unlink className="mr-2 h-4 w-4" />
                              Déconnecter
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Déconnecter la banque</AlertDialogTitle>
                              <AlertDialogDescription>
                                Êtes-vous sûr de vouloir déconnecter cette SCI de sa banque ?
                                Les transactions existantes seront conservées, mais vous ne pourrez plus synchroniser de nouvelles transactions.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => disconnectBank.mutate(sci.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Déconnecter
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
